import React, { useEffect } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Grid from "@mui/material/Grid";
import { makeStyles } from "@mui/styles";
import { Chip, Typography } from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import DottedLinearProgress from "../../../components/shared/dottedLinearProgress";
import { getRiskCriticalContext } from "../../../utils/getCriticalContext";
import { useMediaQuery } from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import { translate } from "../../../utils/utils";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import IconButton from "@mui/material/IconButton";

const useStyles = makeStyles((theme) => ({
  paper: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: "20px",
    padding: "40px",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bgcolor: "background.paper",
    outline: "none",
    p: 4,
    flexGrow: 1,
    minWidth: "70vw",
  },
  card: {
    backgroundColor: "#F3F6F9",
  },
}));

function HeadRow({ label, type }) {
  const { t } = useTranslation();
  return (
    <React.Fragment>
      <Grid item xs={6}>
        <Typography variant="head">{label}</Typography>
      </Grid>

      <Grid
        item
        xs={3}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {type === "asset" && (
          <Typography variant="head">{t("value")}</Typography>
        )}
      </Grid>
      <Grid
        item
        xs={3}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {type === "asset" && (
          <Typography variant="head">{t("level")}</Typography>
        )}
      </Grid>
    </React.Fragment>
  );
}

function FormRow({ item, type }) {
  const criticalContext = getRiskCriticalContext(item.value);
  const { t } = useTranslation();
  return (
    <React.Fragment>
      <Grid item xs={6} style={{ display: "flex", alignItems: "center" }}>
        <Typography variant="body2">
          {item?.[translate("title")] || t("not_specified")}
        </Typography>
      </Grid>
      <Grid
        item
        xs={3}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {type === "asset" && (
          <Typography variant="body2">
            <DottedLinearProgress
              defaultValue={item.value}
              color={criticalContext.colorCode}
            ></DottedLinearProgress>
          </Typography>
        )}
      </Grid>
      {type === "asset" && (
        <Grid
          item
          xs={3}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="body2">
            <Chip
              label={t(criticalContext.label)}
              variant="outlined"
              color={criticalContext.color}
              icon={<FiberManualRecordIcon />}
            />
          </Typography>
        </Grid>
      )}
    </React.Fragment>
  );
}

export default function BasicModal({ open, selectedRisk, handleClose }) {
  const { t } = useTranslation();
  const classes = useStyles();
  const isMobile = useMediaQuery("(max-width:1400px)");
  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box className={classes.paper} id={isMobile && "mobile-content"}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <IconButton onClick={handleClose}>
            <KeyboardBackspaceIcon />
          </IconButton>
          <span
            style={{
              fontFamily: "Barlow Condensed",
              fontSize: "18px",
              fontWeight: "600",
              padding: "0px 16px",
            }}
          >
            {t("back_to_dashboard")}
          </span>
        </div>
        <div>
          {" "}
          <Typography variant="title" sx={{ fontSize: isMobile ? "25px" : {} }}>
            {t("risk_name")} :{" "}
            {selectedRisk?.[translate("title")] || t("not_specified")}
          </Typography>
          <Typography variant="body2" gutterBottom>
            {selectedRisk?.[translate("description")] || t("not_specified")}
          </Typography>
        </div>

        <Grid
          container
          spacing={!isMobile ? 3 : 1}
          sx={{ height: "120px", paddingBottom: "20px" }}
          alignItems="center"
          alignContent="center"
        >
          <Grid item xs={4} sx={{ textAlign: "center" }}>
            <div style={{ borderRight: "2px solid #D9D9D9" }}>
              <Typography variant="subtitle4">Probability</Typography>
              <Typography variant="subtitle1">
                {" "}
                {Math.round(
                  parseFloat(selectedRisk.probability_value) * 100 * 100
                ) / 100}
                %
              </Typography>
            </div>
          </Grid>
          <Grid item xs={4} sx={{ textAlign: "center" }}>
            <div style={{ borderRight: "2px solid #D9D9D9" }}>
              <Typography variant="subtitle4"> Risk</Typography>
              <Typography variant="subtitle1">
                {" "}
                {parseFloat(selectedRisk.risk_value)}/10
              </Typography>
            </div>
          </Grid>
          <Grid
            item
            xs={4}
            sx={{ textAlign: "center" }}
            alignItems="center"
            alignContent="center"
          >
            <div>
              <Typography variant="subtitle4"> Impact</Typography>
              <Typography variant="subtitle1">
                {" "}
                {parseFloat(selectedRisk.impact_value)}/10
              </Typography>
            </div>
          </Grid>
        </Grid>

        <div>
          {!isMobile ? (
            <Box
              sx={{
                p: 2,
                margin: "auto",
                backgroundColor: "#F5F7FB",
              }}
            >
              <div style={{ height: "60px" }}>
                <Grid container item spacing={2}>
                  <HeadRow label={t("assets")} type="asset" />
                </Grid>
              </div>
              <div style={{ maxHeight: "180px", overflow: "auto" }}>
                <Grid container spacing={4}>
                  {selectedRisk.Affected_assets.map((el, index) => (
                    <Grid container item spacing={2} key={`row${index}`}>
                      <FormRow item={el} type="asset" />
                    </Grid>
                  ))}
                </Grid>
              </div>
            </Box>
          ) : (
            <>
              {" "}
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                  sx={{
                    backgroundColor: "#F3FBF9",
                    borderBottom: "none",
                    fontSize: "12px",
                    fontWeight: 600,
                    lineHeight: 1,
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                    color: "#494949",

                    "& .MuiTypography-root": {
                      margin: 0,
                      fontSize: "12px",
                      fontWeight: 900,
                      fontFamily:
                        "Poppins,-apple-system,BlinkMacSystemFont,Segoe UI,Helvetica,Arial,sans-serif,Apple Color Emoji,Segoe UI Emoji",
                      lineHeight: 1.5,
                      letterSpacing: "0.00938em",
                      "& .MuiPaper-root": { boxShadow: "none" },
                    },
                  }}
                >
                  <Typography>
                    ASSETS ({selectedRisk?.Affected_assets?.length}){" "}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>
                    {selectedRisk?.Affected_assets?.map((el) => (
                      <div key={el?.id}>
                        <span
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            padding: "5px 0px",
                          }}
                        >
                          <span
                            style={{
                              color: "rgb(71 138 119)",
                              fontSize: "14px",
                              fontWeight: 800,
                            }}
                          >
                            {el?.title}
                          </span>
                        </span>
                        <span
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            padding: "5px 0px",
                          }}
                        >
                          <span
                            style={{
                              color: "rgb(71 138 119)",
                              fontSize: "14px",
                            }}
                          >
                            Value
                          </span>
                          <span style={{ marginRight: "10px" }}>
                            {" "}
                            {el?.value}
                          </span>
                        </span>
                        <span
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            padding: "5px 0px",
                          }}
                        >
                          <span
                            style={{
                              color: "rgb(71 138 119)",
                              fontSize: "14px",
                            }}
                          >
                            Level{" "}
                          </span>
                          <span>
                            {" "}
                            <Chip
                              label={t(getRiskCriticalContext(el.value)?.label)}
                              variant="outlined"
                              color={getRiskCriticalContext(el.value)?.color}
                              icon={<FiberManualRecordIcon />}
                            />
                          </span>
                        </span>
                        <hr style={{ borderColor: "#B1B4BC5C" }} />{" "}
                      </div>
                    ))}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </>
          )}
          <br></br>
          {!isMobile ? (
            <Box
              sx={{
                p: 2,
                margin: "auto",
                backgroundColor: "#F5F7FB",
              }}
            >
              <div style={{ height: "60px" }}>
                <Grid container item spacing={2}>
                  <HeadRow label={t("weaknesses")} type="weakness" />
                </Grid>
              </div>
              <div style={{ maxHeight: "180px", overflow: "auto" }}>
                <Grid container spacing={4}>
                  {selectedRisk.Related_weaknesses.map((el) => (
                    <Grid container item spacing={2} key={el.id}>
                      <FormRow
                        item={{ ...el, value: el.coeficient }}
                        type="weakness"
                      />
                    </Grid>
                  ))}
                </Grid>
              </div>
            </Box>
          ) : (
            <>
              {" "}
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                  sx={{
                    backgroundColor: "#F3FBF9",
                    borderBottom: "none",
                    fontSize: "12px",
                    fontWeight: 600,
                    lineHeight: 1,
                    letterSpacing: "0.5px",
                    textTransform: "uppercase",
                    color: "#494949",

                    "& .MuiTypography-root": {
                      margin: 0,
                      fontSize: "12px",
                      fontWeight: 900,
                      fontFamily:
                        "Poppins,-apple-system,BlinkMacSystemFont,Segoe UI,Helvetica,Arial,sans-serif,Apple Color Emoji,Segoe UI Emoji",
                      lineHeight: 1.5,
                      letterSpacing: "0.00938em",
                      "& .MuiPaper-root": { boxShadow: "none" },
                    },
                  }}
                >
                  <Typography>
                    Weaknesses ({selectedRisk?.Related_weaknesses?.length}){" "}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>
                    {selectedRisk?.Related_weaknesses?.map((el) => (
                      <div key={el?.id}>
                        <span
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            padding: "5px 0px",
                          }}
                        >
                          <span
                            style={{
                              color: "rgb(71 138 119)",
                              fontSize: "14px",
                              fontWeight: 800,
                            }}
                          >
                            {el?.title}
                          </span>
                        </span>
                        <hr style={{ borderColor: "#B1B4BC5C" }} />{" "}
                      </div>
                    ))}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </>
          )}
        </div>
      </Box>
    </Modal>
  );
}
